import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_ID,
  ErrorHandler,
  EnvironmentProviders,
  Provider,
  provideZonelessChangeDetection,
  importProvidersFrom
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import {
  DateAdapter,
  ErrorStateMatcher,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  ShowOnDirtyErrorStateMatcher
} from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AuthAppService } from './services/auth-app/auth-app.service';
import { BusinessMainService } from './services/business-main.service';
import { ErrorInterceptor } from './services/error-handler';
import { JWTAuthInterceptor } from './services/interceptors/jwt-auth-interceptor';
import { UserService } from './services/user.service';
import { MY_FORMATS } from './shared/date-formats';
import { CanonicalUrlPipe } from './shared/pipes/canonical-url.pipe';

/**
 * Shared between main.ts and main.server.ts (both bootstrapApplication) so the two bootstrap
 * paths can't drift apart now that neither goes through an NgModule.
 */
export function createAppProviders(): (Provider | EnvironmentProviders)[] {
  const sentryProviders: Provider[] = environment.sentry.enabled
    ? [{ provide: Sentry.TraceService, deps: [Router] }]
    : [];

  return [
    provideZonelessChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      RouterModule,
      AppRoutingModule,
      BrowserAnimationsModule,
      GoogleMapsModule,
      MatSnackBarModule
    ),
    { provide: APP_ID, useValue: 'serverApp' },
    AuthAppService,
    UserService,
    BusinessMainService,
    CanonicalUrlPipe,
    { provide: HTTP_INTERCEPTORS, useClass: JWTAuthInterceptor, multi: true },
    // CDK 21 renders overlays as native popovers (browser top layer) by default, which ignores z-index
    // and stacks by open order, pushing snackbars/tooltips behind dialogs. Keep the classic overlay container.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'auto', appearance: 'outline' } },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: ErrorHandler, useClass: ErrorInterceptor },
    ...sentryProviders,
    provideHttpClient(withFetch(), withInterceptorsFromDi())
  ];
}
